"use client";

import { useMemo } from "react";
import type { StateStore } from "../context/state-store";
import type { AtomContext } from "./atoms/registry-types";
import { renderUiNode } from "./atoms/render-ui-node";
import { isPlainObject } from "./StudioScreenRenderer.utils.data";

function lookupTranslation(
 bundle: Record<string, unknown> | undefined,
 key: string,
): string | undefined {
 if (!bundle) return undefined;
 const segments = key.trim().split(".").filter(Boolean);
 if (segments.length ===0) return undefined;
 let current: unknown = bundle;
 for (const segment of segments) {
 if (!isPlainObject(current)) return undefined;
 current = current[segment];
 }
 return typeof current === "string" ? current : undefined;
}

export function useAtomContext(options: {
 store: StateStore;
 fireTrigger: AtomContext["fireTrigger"];
 setFormValue: AtomContext["setFormValue"];
 getFormValues: AtomContext["getFormValues"];
 translations: Record<string, unknown> | undefined;
 /** URL-derived locale (e.g. "fr" for /fr/features) for crawlable hrefs. */
 urlLocale?: string;
}): AtomContext {
 const { store, fireTrigger, setFormValue, getFormValues, translations, urlLocale } = options;
 return useMemo<AtomContext>(() => {
 const atomContext: AtomContext = {
 resolveRef: (refValue: unknown) =>
 typeof refValue === "string" ? store.readPath(refValue) : refValue,
 fireTrigger,
 renderChildren: (children): ReturnType<AtomContext["renderChildren"]> =>
 children?.map((child): ReturnType<typeof renderUiNode> =>
 renderUiNode(child, atomContext),
 ) ?? [],
 setFormValue,
 getFormValues,
 translate: (key: string) => lookupTranslation(translations, key),
 urlLocale,
 };
 return atomContext;
 }, [store, fireTrigger, setFormValue, getFormValues, translations, urlLocale]);
}